import {
  GroupBox,
  HeaderRowWrapper,
  InputsWrapper,
  RuleWarningContainer,
} from '../atoms';
import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import { GhostButton, DragButton } from '@ardoq/button';
import { DeleteIcon } from '@ardoq/icons';
import { GroupType } from '@ardoq/api-types';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { ReferenceTypeGroupRule } from './GroupingRules/ReferenceTypeGroupRule';
import { ParentAllGroupRule } from './GroupingRules/ParentAllGroupRule';
import { ChildGroupRule } from './GroupingRules/ChildGroupRule';
import { TagGroupRule } from './GroupingRules/TagGroupRule';
import { FieldGroupRule } from './GroupingRules/FieldGroupRule';
import { ParentGroupRule } from './GroupingRules/ParentGroupRule';
import { GroupingRuleLabel } from './GroupingRuleLabel';
import {
  PopoverContentType,
  WithPopoverPlacement,
  WithPopover,
} from '@ardoq/popovers';
import { RuleNotificationType } from '@ardoq/query-builder';
import { WarningNotification } from '@ardoq/status-ui';
import {
  perspectivesRowDragAndDropHandlers,
  GetInvalidGroupingSelectOptions,
  GetRuleNotificationParams,
  PerspectiveGroupingRule,
  PerspectivesGroupsOptions,
} from '@ardoq/perspectives';
import { ReferenceTypeNameGroupRule } from './GroupingRules/ReferenceTypeNameGroupRule';
import { type I18nLocale, t } from '@ardoq/i18n';

type GroupProps = {
  dragAndDropCardId: string; // for drag&drop only
  popoverContent?: PopoverContentType;
  groupingRule: PerspectiveGroupingRule;
  groupingOptions: PerspectivesGroupsOptions;
  getRuleNotificationParams?: GetRuleNotificationParams;
  onGroupingRuleDeleted: (groupingRuleId: string) => void;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  isDisabled?: boolean;
  /**
   * Whether the REFERENCE_TYPE_NAME grouping rule type should render its
   * dedicated input. Wired from the consuming app's feature flag
   * (Features.GROUPING_BY_TYPE_NAMES).
   */
  isGroupingByTypeNamesEnabled?: boolean;
  i18nLocale?: I18nLocale;
};

const Group = ({
  groupingRule,
  popoverContent,
  groupingOptions,
  onGroupingRuleDeleted,
  onGroupingRuleUpdated,
  getRuleNotificationParams,
  getInvalidGroupingSelectOptions,
  isDisabled,
  isGroupingByTypeNamesEnabled = false,
  i18nLocale = {},
}: GroupProps) => {
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(
      groupingRule.id || null,
      useDragAndDropContext()
    );

  const ruleNotificationParams = getRuleNotificationParams?.(groupingRule);
  const warningMessage =
    ruleNotificationParams?.notificationType === RuleNotificationType.WARNING
      ? ruleNotificationParams.message
      : null;

  const isDraggable = !!groupingRule.id && !isDisabled;

  const fieldsTabIndex = isDisabled ? -1 : 0;

  const type = groupingRule.type;

  const hasNoInputs =
    type === GroupType.WORKSPACE || type === GroupType.COMPONENT;

  return (
    <WithPopover
      content={popoverContent}
      preferredPlacement={WithPopoverPlacement.TOP}
    >
      <GroupBox
        data-testid={`group-box-group-by-${type}`}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        flexDirection="column"
      >
        <HeaderRowWrapper>
          <DragButton
            draggable={isDraggable}
            isDisabled={isDisabled}
            data-testid="drag-button"
            i18nLocale={i18nLocale}
          />
          <GroupingRuleLabel groupType={type} isDisabled={isDisabled} />
          <GhostButton
            dataTestId="delete-grouping-rule"
            onClick={() => onGroupingRuleDeleted(groupingRule.id)}
          >
            <VisuallyHidden>
              {t(
                i18nLocale,
                'perspectivesSidebar:groupingRules.deleteGrouping',
                'Delete grouping'
              )}
            </VisuallyHidden>
            <DeleteIcon />
          </GhostButton>
        </HeaderRowWrapper>

        {warningMessage && (
          <RuleWarningContainer data-testid="group-box-grouping-warning-notification">
            <WarningNotification>{warningMessage}</WarningNotification>
          </RuleWarningContainer>
        )}

        {!hasNoInputs && (
          <InputsWrapper data-testid="inputs-wrapper" $isDisabled={isDisabled}>
            {type === GroupType.REFERENCE && (
              <ReferenceTypeGroupRule
                onGroupingRuleUpdated={onGroupingRuleUpdated}
                groupingRule={groupingRule}
                workspaces={groupingOptions.workspaces}
                tabIndex={fieldsTabIndex}
                referenceTypesPerWorkspace={
                  groupingOptions.referenceTypesPerWorkspace
                }
                getInvalidGroupingSelectOptions={
                  getInvalidGroupingSelectOptions
                }
                i18nLocale={i18nLocale}
              />
            )}
            {isGroupingByTypeNamesEnabled &&
              type === GroupType.REFERENCE_TYPE_NAME && (
                <ReferenceTypeNameGroupRule
                  onGroupingRuleUpdated={onGroupingRuleUpdated}
                  groupingRule={groupingRule}
                  globalReferenceTypeNames={
                    groupingOptions.globalReferenceTypeNames
                  }
                  tabIndex={fieldsTabIndex}
                  getInvalidGroupingSelectOptions={
                    getInvalidGroupingSelectOptions
                  }
                />
              )}
            {type === GroupType.PARENT && (
              <ParentGroupRule
                onGroupingRuleUpdated={onGroupingRuleUpdated}
                groupingRule={groupingRule}
                workspaces={groupingOptions.workspaces}
                tabIndex={fieldsTabIndex}
                componentTypesPerWorkspace={
                  groupingOptions.componentTypesPerWorkspace
                }
                getInvalidGroupingSelectOptions={
                  getInvalidGroupingSelectOptions
                }
                i18nLocale={i18nLocale}
              />
            )}
            {type === GroupType.PARENT_ALL && (
              <ParentAllGroupRule
                tabIndex={fieldsTabIndex}
                groupingRule={groupingRule}
                onGroupingRuleUpdated={onGroupingRuleUpdated}
                i18nLocale={i18nLocale}
              />
            )}
            {type === GroupType.CHILD && (
              // ChildGroupingRule has been deprecated. User cannot create a new grouping rule of this type.
              // User still can display such rule if it's part of a saved perspective, but it's read-only and cannot be updated.
              <ChildGroupRule
                groupingRule={groupingRule}
                workspaces={groupingOptions.workspaces}
                componentTypesPerWorkspace={
                  groupingOptions.componentTypesPerWorkspace
                }
                i18nLocale={i18nLocale}
              />
            )}
            {type === GroupType.FIELD && (
              <FieldGroupRule
                onGroupingRuleUpdated={onGroupingRuleUpdated}
                groupingRule={groupingRule}
                workspaces={groupingOptions.workspaces}
                fieldsPerWorkspace={groupingOptions.fieldsPerWorkspace}
                tabIndex={fieldsTabIndex}
                getInvalidGroupingSelectOptions={
                  getInvalidGroupingSelectOptions
                }
                i18nLocale={i18nLocale}
              />
            )}
            {type === GroupType.TAG && (
              <TagGroupRule
                onGroupingRuleUpdated={onGroupingRuleUpdated}
                groupingRule={groupingRule}
                workspaces={groupingOptions.workspaces}
                tagsPerWorkspace={groupingOptions.tagsPerWorkspace}
                tabIndex={fieldsTabIndex}
                getInvalidGroupingSelectOptions={
                  getInvalidGroupingSelectOptions
                }
                i18nLocale={i18nLocale}
              />
            )}
          </InputsWrapper>
        )}
      </GroupBox>
    </WithPopover>
  );
};

export { Group };
